import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { Select, react } from "uikit";
import { useTranslation } from "react-i18next";

import PaymentSystem, {
	PaymentCurrency,
} from "../../../../../../../../services/PaymentSystem";
import useObjectEditor from "../../../../../../../../hooks/useObjectEditor";
import translationPath from "../../../../constants/translationPath";

import InternalController from "./Controller";

const PlatonPaymentsCurrencyBase = react.withController<
	PlatonPaymentsCurrency.PropsBase,
	PlatonPaymentsCurrency.Controller
>(({ value, onChange, controller }) => {
	const [error, setError] = useState<boolean>(false);

	const { t } = useTranslation();

	const valueEditor = useObjectEditor(value, onChange);

	const platonCurrency = valueEditor.useGetter("platonCurrency");
	const platonCurrencySet = valueEditor.useSetter("platonCurrency");

	const selectOptionsWrap = useCallback(
		(item: { id: PaymentCurrency; label: PaymentCurrency }) => ({
			key: item.id,
			value: item.id,
			label: item.label,
		}),
		[],
	);

	const options = useMemo(
		() =>
			[PaymentCurrency.UAH].map((label) =>
				selectOptionsWrap({
					id: label,
					label,
				}),
			),
		[selectOptionsWrap],
	);

	const updateSelectedValues = useCallback(
		(value) => {
			platonCurrencySet(value);
			setError(false);
		},
		[platonCurrencySet],
	);

	const textError = useMemo(() => {
		if (!error) return "";
		return t([
			`${translationPath}.errors.currency`,
			"Currency not selected",
		]);
	}, [t, error]);

	const placeholder = useMemo(
		() =>
			t([
				`${translationPath}.placeholders.currency`,
				"Choose a currency",
			]),
		[t],
	);

	controller.setContext({ value, setError });
	return (
		<Select
			style={{ width: "200px" }}
			placeholder={placeholder}
			error={textError}
			value={platonCurrency}
			options={options}
			onChange={updateSelectedValues}
		/>
	);
}, InternalController);
const PlatonPaymentsCurrency = memo(PlatonPaymentsCurrencyBase);

declare namespace PlatonPaymentsCurrency {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default PlatonPaymentsCurrency;
